/**
 * Master-Sprint P0 (2026-06) — compact mobile-web heatmap controls.
 *
 * On mobile the map must own the viewport: only the primary market
 * modes stay visible (vertical, layer, market status, location, "more
 * filters" with badge). Everything else lives in the bottom sheet
 * (HeatmapMobileFilterSheet) — SAME business logic as desktop, the
 * sheet just re-renders the shared chip editors against a draft state.
 */
import React from "react";
import { Car, Home, MapPin, SlidersHorizontal, Sparkles, X } from "lucide-react";
import { MODES, StatusSegment } from "./HeatmapChipBar";

const SEG_ON = "border-amber-500/60 bg-amber-500/15 text-amber-300";
const SEG_OFF = "border-zinc-800 bg-zinc-950 text-zinc-400";

export default function HeatmapMobileControls({
  vertical,
  mode,
  filters,
  onVertical,
  onMode,
  onPatch,
  onOpenFilters,
  activeFilterCount,
  ctxBanner,
  onClearContext,
  t,
}) {
  return (
    <div data-testid="heatmap-mobile-controls" className="space-y-2 md:hidden">
      {/* Row 1 — vertical + layer modes (+ buy/rent for immo) */}
      <div className="no-scrollbar flex items-center gap-1.5 overflow-x-auto pb-0.5">
        <div className="flex shrink-0 overflow-hidden rounded-full border border-zinc-800 bg-zinc-950">
          {[
            { v: "auto", Icon: Car, label: t("chips.vertAuto") },
            { v: "immo", Icon: Home, label: t("chips.vertImmo") },
          ].map(({ v, Icon, label }) => (
            <button
              key={v}
              type="button"
              data-testid={`heatmap-m-vertical-${v}`}
              aria-pressed={vertical === v}
              onClick={() => onVertical(v)}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${
                vertical === v ? "bg-amber-500/15 text-amber-300" : "text-zinc-500"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
        {vertical === "immo" && (
          <div className="flex shrink-0 overflow-hidden rounded-full border border-zinc-800 bg-zinc-950">
            {["buy", "rent"].map((tt) => (
              <button
                key={tt}
                type="button"
                data-testid={`heatmap-m-tt-${tt}`}
                aria-pressed={filters.tt === tt}
                onClick={() => onPatch({ tt })}
                className={`px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${
                  filters.tt === tt ? "bg-amber-500/15 text-amber-300" : "text-zinc-500"
                }`}
              >
                {t(`chips.${tt}`)}
              </button>
            ))}
          </div>
        )}
        <div className="h-5 w-px shrink-0 bg-zinc-800" aria-hidden="true" />
        {MODES[vertical].map((m) => (
          <button
            key={m.id}
            type="button"
            data-testid={`heatmap-m-mode-${m.id}`}
            aria-pressed={mode === m.id}
            onClick={() => onMode(m.id)}
            className={`shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${
              mode === m.id ? SEG_ON : SEG_OFF
            }`}
          >
            <span aria-hidden="true">{m.icon}</span> {t(`chips.mode_${m.id}`)}
          </button>
        ))}
      </div>

      {/* Row 2 — market status (markt mode) + location + more filters */}
      <div className="no-scrollbar flex items-center gap-1.5 overflow-x-auto pb-0.5">
        {mode === "markt" && (
          <StatusSegment
            compact
            status={filters.status || ""}
            onStatus={(s) => onPatch({ status: s })}
            t={t}
          />
        )}
        <button
          type="button"
          data-testid="heatmap-m-region-chip"
          onClick={onOpenFilters}
          className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
            filters.region ? SEG_ON : SEG_OFF
          }`}
        >
          <MapPin className="h-3 w-3" />
          {filters.region || t("chips.region")}
        </button>
        <button
          type="button"
          data-testid="heatmap-m-more-filters"
          onClick={onOpenFilters}
          className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-[11px] font-semibold text-zinc-200 transition-colors hover:border-amber-500/50"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          {t("chips.moreFilters")}
          {activeFilterCount > 0 && (
            <span
              data-testid="heatmap-m-filter-badge"
              className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold leading-none text-zinc-950"
            >
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {ctxBanner && (
        <div
          data-testid="heatmap-m-ctx-chip"
          className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] text-emerald-300"
        >
          <Sparkles className="h-3 w-3 shrink-0" />
          <span className="truncate">{ctxBanner.label || t("ctxApplied")}</span>
          <button
            type="button"
            data-testid="heatmap-m-ctx-clear"
            onClick={onClearContext}
            className="shrink-0 text-emerald-400/70 hover:text-emerald-200"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}
